import React, { useEffect, useRef, useState } from 'react';
import Box from '@amzn/meridian/box';
import {
  ordisAPIService,
  JobStatusResponse,
  JobStatusNotFoundError,
} from '../../service/ordisApiService';
import './JobStatusCard.css';

interface JobStatusCardProps {
  jobId: string;
}

// Poll cadence — short enough that the median MSSQL install (60-90s) is
// reflected within one full poll, long enough that we don't hammer the
// Query Resolver for jobs that hold IN_PROGRESS for the entire 120s
// budget. Aligns with the chatbot's "ask back in ~90 seconds" copy.
const POLL_INTERVAL_MS = 8000;
// Hard cap so a stuck job can't keep the timer alive forever. The
// underlying record TTL is 24h but the worker has a 300s function
// timeout — anything past 6 minutes is almost certainly the worker
// having crashed before writing FAILED.
const POLL_MAX_DURATION_MS = 6 * 60 * 1000;

const STATUS_LABELS: Record<string, string> = {
  IN_PROGRESS: 'Running',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
  UNKNOWN: 'Unknown',
};

const STATUS_ICONS: Record<string, string> = {
  IN_PROGRESS: '⏱',
  COMPLETED: '✓',
  FAILED: '✗',
  UNKNOWN: '…',
};

const JobStatusCard: React.FC<JobStatusCardProps> = ({ jobId }) => {
  const [job, setJob] = useState<JobStatusResponse | null>(null);
  const [pollingError, setPollingError] = useState<string | null>(null);
  const [stopped, setStopped] = useState(false);
  // Wall-clock start so the spinner shows ``Xs elapsed`` even before the
  // first poll lands. The backend also returns elapsed_seconds; we prefer
  // its value once we have it.
  const startRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!jobId) return undefined;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      try {
        const result = await ordisAPIService.getJobStatus(jobId);
        if (cancelled) return;
        setJob(result);
        setPollingError(null);
        if (result.status === 'COMPLETED' || result.status === 'FAILED') {
          setStopped(true);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof JobStatusNotFoundError) {
          // 404 most often means the worker has not yet committed the
          // record. Don't show an error panel for the first ~10s; only
          // surface it if it persists past a reasonable initial delay.
          if (Date.now() - startRef.current > 15000) {
            setPollingError(
              `No job found with id ${jobId.slice(0, 8)} (it may have been cleaned up).`,
            );
            setStopped(true);
            return;
          }
        } else {
          // Transient errors keep us polling but surface a yellow note.
          setPollingError(
            err instanceof Error ? err.message : 'Status lookup failed; retrying.',
          );
        }
      }

      if (Date.now() - startRef.current > POLL_MAX_DURATION_MS) {
        setStopped(true);
        setPollingError(
          'Stopped polling — the operation has been running for more than 6 minutes.',
        );
        return;
      }

      timer = setTimeout(tick, POLL_INTERVAL_MS);
    };

    tick();

    return () => {
      cancelled = true;
      if (timer !== undefined) {
        clearTimeout(timer);
      }
    };
  }, [jobId]);

  const status = job?.status ?? 'IN_PROGRESS';
  const elapsed = job?.elapsed_seconds ?? Math.floor((Date.now() - startRef.current) / 1000);
  const shortId = jobId.slice(0, 8);

  return (
    <Box
      className={`job-status-card job-status-card--${status.toLowerCase()}`}
      data-testid="job-status-card"
    >
      <Box className="job-status-card__header">
        <span className="job-status-card__icon">{STATUS_ICONS[status] ?? '…'}</span>
        <span className="job-status-card__label">{STATUS_LABELS[status] ?? status}</span>
        <span className="job-status-card__id">#{shortId}</span>
        {!stopped && (
          <span className="job-status-card__elapsed" aria-label="elapsed seconds">
            {elapsed}s
          </span>
        )}
      </Box>

      <Box className="job-status-card__body">
        {job?.operation_type && (
          <div className="job-status-card__op">
            {job.operation_type.replace(/_/g, ' ')}
            {job.server_name ? ` · ${job.server_name}` : ''}
          </div>
        )}

        {status === 'IN_PROGRESS' && (
          <div className="job-status-card__progress">
            Working in the background — usually 60-90 seconds.
          </div>
        )}

        {status === 'COMPLETED' && job?.result && (
          <div className="job-status-card__result">{job.result}</div>
        )}

        {status === 'FAILED' && (
          <div className="job-status-card__error">{job?.error ?? 'Unknown error.'}</div>
        )}

        {pollingError && (
          <div className="job-status-card__warning">{pollingError}</div>
        )}
      </Box>
    </Box>
  );
};

export default JobStatusCard;
